import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'

const ORANGE = 0xffa500
const SIZE = 1.05
const EDGE_RADIUS = 0.032
const NODE_OUTER = 0.1
/** Radio del agujero — bien visible respecto al anillo */
const NODE_INNER = 0.055

const CORNERS = [
  [-1, -1, -1],
  [-1, -1, 1],
  [-1, 1, -1],
  [-1, 1, 1],
  [1, -1, -1],
  [1, -1, 1],
  [1, 1, -1],
  [1, 1, 1],
]

const EDGE_PAIRS = [
  [0, 1],
  [0, 2],
  [0, 4],
  [1, 3],
  [1, 5],
  [2, 3],
  [2, 6],
  [3, 7],
  [4, 5],
  [4, 6],
  [5, 7],
  [6, 7],
]

const addThickEdge = (group, a, b, material, disposables) => {
  const start = new THREE.Vector3(...a).multiplyScalar(SIZE / 2)
  const end = new THREE.Vector3(...b).multiplyScalar(SIZE / 2)
  const dir = new THREE.Vector3().subVectors(end, start)
  const lengthFull = dir.length()
  // Acortar aristas para que no tapen el agujero del anillo
  const trim = NODE_OUTER * 0.75
  const length = Math.max(lengthFull - trim * 2, 0.01)
  dir.normalize()
  const mid = start.clone().add(end).multiplyScalar(0.5)

  const geometry = new THREE.CylinderGeometry(
    EDGE_RADIUS,
    EDGE_RADIUS,
    length,
    10,
  )
  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.copy(mid)
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir)
  group.add(mesh)
  disposables.push(geometry)
}

/**
 * Cubo Lucía: aristas con grosor + anillos huecos en vértices.
 */
const LuciaCube = ({ size = 88, className }) => {
  const mountRef = useRef(null)

  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return undefined

    const width = size
    const height = size
    const disposables = []

    const scene = new THREE.Scene()

    const camera = new THREE.PerspectiveCamera(30, width / height, 0.1, 100)
    camera.position.set(2.55, 2.2, 2.55)
    camera.lookAt(0, 0, 0)

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'low-power',
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setSize(width, height)
    renderer.setClearColor(0x000000, 0)
    mount.appendChild(renderer.domElement)

    const group = new THREE.Group()
    scene.add(group)

    const edgeMat = new THREE.MeshBasicMaterial({ color: ORANGE })
    disposables.push(edgeMat)
    EDGE_PAIRS.forEach(([i, j]) => {
      addThickEdge(group, CORNERS[i], CORNERS[j], edgeMat, disposables)
    })

    // Anillos huecos: corona naranja + disco negro en el centro
    // (oculta uniones de aristas; se lee como hueco sobre el fondo negro).
    const ringGeo = new THREE.RingGeometry(NODE_INNER, NODE_OUTER, 48)
    const ringMat = new THREE.MeshBasicMaterial({
      color: ORANGE,
      side: THREE.DoubleSide,
    })
    const holeGeo = new THREE.CircleGeometry(NODE_INNER * 1.02, 48)
    const holeMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.DoubleSide,
    })
    disposables.push(ringGeo, ringMat, holeGeo, holeMat)

    const half = SIZE / 2
    const nodes = []
    CORNERS.forEach(([x, y, z]) => {
      const node = new THREE.Group()
      node.position.set(x * half, y * half, z * half)

      const hole = new THREE.Mesh(holeGeo, holeMat)
      hole.renderOrder = 1
      node.add(hole)

      const ring = new THREE.Mesh(ringGeo, ringMat)
      ring.renderOrder = 2
      // Evita z-fighting con el disco del agujero
      ring.position.z = 0.001
      node.add(ring)

      group.add(node)
      nodes.push(node)
    })

    group.rotation.set(0.12, -0.55, 0.08)

    let frameId = 0
    let running = true
    const clock = new THREE.Clock()
    const parentWorldQuat = new THREE.Quaternion()
    const billboard = new THREE.Quaternion()

    const animate = () => {
      if (!running) return
      frameId = requestAnimationFrame(animate)
      const t = clock.getElapsedTime()
      group.rotation.y = -0.55 + t * 0.35
      group.position.y = Math.sin(t * 1.1) * 0.04

      group.getWorldQuaternion(parentWorldQuat)
      billboard.copy(parentWorldQuat).invert().multiply(camera.quaternion)
      nodes.forEach((node) => {
        node.quaternion.copy(billboard)
      })

      renderer.render(scene, camera)
    }
    animate()

    return () => {
      running = false
      cancelAnimationFrame(frameId)
      disposables.forEach((d) => d.dispose?.())
      renderer.dispose()
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement)
      }
    }
  }, [size])

  return (
    <div
      ref={mountRef}
      className={className}
      style={{ width: size, height: size, lineHeight: 0 }}
      aria-hidden
    />
  )
}

export default LuciaCube
